import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Plus, Trash2, Save, X, Eye, EyeOff, Image as ImageIcon, Tag, Edit3, Link2, ChevronDown, ChevronRight, Upload, Check } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { neonQuery } from '../../services/api';
import { fetchAllPages } from './siteContentShared';
import MediaPicker from '../../components/MediaPicker';
import { getCurrentUser, getAccessToken } from '../../services/auth';

// ── Constants ────────────────────────────────────────────────────────────────
//
// Replaces the previous dev's external recipes webapp. Backend lives in
// netlify/functions/neon.js (cp_recipes / cp_recipe_tag_categories /
// cp_recipe_tags / cp_recipe_tag_links tables) and the public read API at
// netlify/functions/recipes.js + recipe-tags.js. The storefront fetches
// from the public functions; this admin talks to neon directly via
// neonQuery for richer write paths.

// Quill toolbar — minimal, recipe-author-friendly. No fancy block embeds.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

// Helper — slugify a name for the URL path. Mirrors makeSlug() in
// netlify/functions/neon.js so admin previews match what the backend will store.
function slugify(input) {
  return String(input || '').toLowerCase().trim()
    .replace(/^\/+|\/+$/g, '')
    .replace(/[^a-z0-9\-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Drop the { data, meta } wrapper that recipes_list/_get/_upsert return —
// neonQuery passes the whole object through (since it's not a `rows`
// response), so we unwrap consistently here.
function unwrapData(r) { return r?.data ?? r; }

// Build the absolute URL for an R2 key. Recipes are read from the storefront
// (crabplace.com / sandbox), so URLs must be absolute, not site-relative.
const R2_PUBLIC_HOST = 'https://cpx-prototype.netlify.app';
function r2KeyToUrl(key) {
  if (!key) return null;
  return `${R2_PUBLIC_HOST}/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
}
// Pull the best displayable URL out of a media_assets row (which only stores
// r2_original_key / r2_thumb_key — never a `.url` column).
function urlFromMediaRow(row) {
  if (!row) return null;
  return r2KeyToUrl(row.r2_original_key) || r2KeyToUrl(row.r2_thumb_key) || row.url || row.thumb_url || null;
}

// Mirrors MediaLibrary's fileToResizedBase64 so drag-and-drop uploads here
// route through the same /media-library-upload endpoint (R2 + classify + dedupe).
const UPLOAD_ORIGINAL_MAX_EDGE = 2048;
const UPLOAD_THUMB_MAX_EDGE = 480;
const UPLOAD_ORIGINAL_QUALITY = 0.88;
const UPLOAD_THUMB_QUALITY = 0.78;
async function fileToResizedBase64(file, maxEdge, quality) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image (HEIC and RAW not supported — convert to JPEG/PNG first)'));
    el.src = dataUrl;
  });
  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = Math.min(1, maxEdge / longEdge);
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL('image/jpeg', quality);
  return { base64: jpeg.slice(jpeg.indexOf(',') + 1), naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight };
}

// Only YouTube gets an embedded player on the recipe page. Everything else is
// served as a real <video>, so the admin labels the two differently rather than
// implying they behave the same.
const isYouTubeUrl = (u) => /(?:youtube\.com|youtu\.be)/i.test(String(u || ''));

// ── Main component ───────────────────────────────────────────────────────────
export default function RecipesAdmin() {
  const [recipes, setRecipes] = useState([]);
  const [tagsTree, setTagsTree] = useState([]); // [{ id, custom_title, tag_rows: [{id,name,slug}] }]
  const [bcProducts, setBcProducts] = useState([]); // for SKU typeahead
  const [bcLoading, setBcLoading] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [edits, setEdits] = useState(null); // null = no recipe selected, object = pending edits
  const [savingRecipe, setSavingRecipe] = useState(false);

  const [search, setSearch] = useState('');
  const [filterVisible, setFilterVisible] = useState(''); // '' | 'visible' | 'hidden'
  const [filterTagIds, setFilterTagIds] = useState(new Set());

  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  // Separate picker state for video, so opening one cannot hand a clip to the
  // images array or a photo to the videos array.
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [status, setStatus] = useState(null);
  const flashStatus = (type, msg, ms = 3500) => {
    setStatus({ type, msg });
    if (ms) setTimeout(() => setStatus((s) => (s?.msg === msg ? null : s)), ms);
  };

  // Where each recipe stands along SB-2 -> SB-1 -> PROD. Advisory only: a failure
  // here must never block editing, so errors are swallowed.
  const [promotion, setPromotion] = useState({ byId: {}, sources: [] });
  const loadPromotion = useCallback(async () => {
    try {
      const r = await neonQuery('recipe_promotion_status', {});
      const body = (r && r.rows) ? r : (unwrapData(r) || {});
      const byId = {};
      (body.rows || []).forEach((row) => { byId[row.id] = row; });
      setPromotion({ byId, sources: body.sources || [] });
    } catch (e) { /* advisory */ }
  }, []);
  useEffect(() => { loadPromotion(); }, [loadPromotion]);

  // Promote the SAVED version one step. SB-1 takes what SB-2 has; production
  // takes what SB-1 has. Unsaved edits are not included, so say so first.
  const promote = async (to, ids, label) => {
    if (ids && ids.length === 1 && typeof hasUnsavedChanges === 'function' && hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Promote the last SAVED version anyway?')) return;
    }
    const target = to === 'prod' ? 'PRODUCTION (crabplace.com)' : 'SB-1';
    const what = ids && ids.length === 1 ? '"' + label + '"' : (ids ? ids.length + ' recipes' : 'ALL recipes');
    const warn = to === 'prod'
      ? '\n\nProduction gets the version that is on SB-1 now. It goes live on crabplace.com and its recipe page is republished.'
      : '\n\nSB-1 gets the saved version from SB-2, and its recipe page is republished.';
    if (!window.confirm('Promote ' + what + ' to ' + target + '?' + warn)) return;
    try {
      const resp = await fetch('/.netlify/functions/recipe-promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          ids,
          promoted_by: getCurrentUser()?.email || null,
          confirm: to === 'prod' ? 'prod' : undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || ('HTTP ' + resp.status));
      const n = (data.promoted || []).length;
      const removedNote = (data.removed || []).length ? ', removed ' + data.removed.length : '';
      const missingNote = (data.notInSource || []).length ? ' (' + data.notInSource.length + ' not on SB-1 yet)' : '';
      flashStatus(n ? 'success' : 'error', 'Promoted ' + n + ' to ' + (to === 'prod' ? 'PROD' : 'SB-1') + removedNote + missingNote, 7000);
      await loadPromotion();
    } catch (e) { flashStatus('error', 'Promote failed: ' + e.message, 8000); }
  };

  // ── Data loading ────────────────────────────────────────────────────────────
  const loadRecipes = useCallback(async () => {
    try {
      // The list filters/searches CLIENT-SIDE, so we must load EVERY recipe.
      // recipes_list caps a page at 200 rows — with >200 recipes, anything past
      // position 200 (name-sorted) was invisible to search/filter (e.g. a recipe
      // named "Spicy…" or any of the newest adds). Page through until exhausted.
      const pageSize = 200;
      let all = [];
      let total = Infinity;
      for (let page = 1; all.length < total && page <= 50; page++) {
        const r = await neonQuery('recipes_list', { limit: pageSize, page, sort: 'name', order: 1 });
        const data = unwrapData(r) || [];
        const count = r?.meta?.count;
        total = typeof count === 'number' ? count : all.length + data.length;
        all = all.concat(data);
        if (data.length < pageSize) break;
      }
      setRecipes(all);
    } catch (e) { flashStatus('error', `Load recipes failed: ${e.message}`); }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const r = await neonQuery('recipe_tags_list');
      // recipe_tags_list returns { data: [...] }; unwrap.
      setTagsTree(unwrapData(r) || []);
    } catch (e) { flashStatus('error', `Load tags failed: ${e.message}`); }
  }, []);

  const loadBcProducts = useCallback(async () => {
    setBcLoading(true);
    try {
      // is_visible=true skips legacy hidden parent products. Several lobster /
      // crab cake families exist as both a current grouped-variant product
      // (visible=true) and older per-size products (visible=false) — without
      // this filter the typeahead surfaces the legacy ones (MDLOB, SLLOB, ...)
      // alongside the canonical entry (LOB) and confuses recipe linking.
      // custom_url is included so we can link the storefront recipe page
      // straight to the product's PDP — /search.php?search_query=<sku> only
      // auto-redirects on a single match, and BC's prefix search means SKUs
      // like "LOB" hit LOB/LOBCW/LOBPL and land on a search results page.
      const products = await fetchAllPages('/v3/catalog/products?include_fields=name,sku,custom_url&is_visible=true');
      setBcProducts(products.map(p => ({
        id: p.id,
        sku: p.sku || '',
        name: p.name,
        url: p.custom_url?.url || '',
      })));
    } catch (e) { flashStatus('error', `Load products failed: ${e.message}`); }
    finally { setBcLoading(false); }
  }, []);

  useEffect(() => { loadRecipes(); loadTags(); loadBcProducts(); }, [loadRecipes, loadTags, loadBcProducts]);

  // ── Editor lifecycle ────────────────────────────────────────────────────────
  const openRecipe = (recipe) => {
    if (edits && hasUnsavedChanges()) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setSelectedId(recipe.id);
    setEdits({
      ...recipe,
      // Coerce missing fields to safe defaults so controlled inputs don't churn
      custom_title: recipe.custom_title || '',
      author: recipe.author || '',
      time: recipe.time || '',
      introduction: recipe.introduction || '',
      instructions: recipe.instructions || '',
      ingredients: recipe.ingredients || '',
      servings: recipe.servings || '',
      images: recipe.images || [],
      videos: recipe.videos || [],
      skus: recipe.skus || [],
      visible: recipe.visible !== false,
      sort_order: recipe.sort_order || 0,
      published_date: recipe.published_date || '',
      // Derive the currently-applied tag ids from the grouped tags object
      tag_ids: derivetagIdsFromGrouped(recipe.tags, tagsTree),
    });
  };

  const newRecipe = () => {
    if (edits && hasUnsavedChanges()) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setSelectedId(null);
    setEdits({
      id: null, name: '', slug: '', custom_title: '', author: '', time: '',
      introduction: '', instructions: '', ingredients: '', servings: '',
      images: [], videos: [], skus: [], visible: true,
      sort_order: 0, published_date: new Date().toISOString().slice(0, 10),
      tag_ids: [],
    });
  };

  // An imported recipe opens as an UNSAVED new recipe, exactly as if it had
  // been typed in by hand. Nothing is written to the database until she presses
  // Save, so a bad parse costs a glance rather than a live recipe on the site.
  const newRecipeFromImport = (r) => {
    if (edits && hasUnsavedChanges()) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setSelectedId(null);
    setEdits({
      id: null, name: r.name || '', slug: '', custom_title: '', author: '',
      time: r.time || '',
      introduction: r.introduction || '',
      instructions: r.instructions || '',
      ingredients: r.ingredients || '',
      servings: r.servings || '',
      images: [], videos: [], skus: [], visible: true,
      sort_order: 0, published_date: new Date().toISOString().slice(0, 10),
      tag_ids: [],
      // Carried for display only. Deliberately NOT applied as real tags: recipe
      // tags are a curated list with its own categories, and minting new ones
      // from whatever a Word file happened to say is how that list turns to
      // soup. She picks the real ones from the list below.
      _importedTags: r.suggestedTags || [],
      _importedMissing: r.missing || [],
    });
    setStatus({
      type: 'ok',
      text: (r.missing && r.missing.length)
        ? 'Read it in. It could not find: ' + r.missing.join(', ') + '. Fill those in, add the photos, then Save.'
        : 'Read it in. Check it over, add the photos, then Save.',
    });
  };

  const closeEditor = () => {
    if (edits && hasUnsavedChanges()) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setSelectedId(null);
    setEdits(null);
  };

  // We can't compare against the original recipe object directly because we
  // mutate `edits` freely. Instead, look up the row from `recipes` by id.
  function hasUnsavedChanges() {
    if (!edits) return false;
    if (!edits.id) return true; // new recipe, always "unsaved"
    const orig = recipes.find(r => r.id === edits.id);
    if (!orig) return true;
    const origIds = derivetagIdsFromGrouped(orig.tags, tagsTree).slice().sort();
    const editIds = (edits.tag_ids || []).slice().sort();
    if (JSON.stringify(origIds) !== JSON.stringify(editIds)) return true;
    const fields = ['name','slug','custom_title','author','time','introduction','instructions','ingredients','servings','visible','sort_order','published_date'];
    for (const f of fields) {
      if ((edits[f] || '') !== (orig[f] || '')) return true;
    }
    if (JSON.stringify(edits.images || []) !== JSON.stringify(orig.images || [])) return true;
    if (JSON.stringify(edits.videos || []) !== JSON.stringify(orig.videos || [])) return true;
    if (JSON.stringify(edits.skus || []) !== JSON.stringify(orig.skus || [])) return true;
    return false;
  }

  const patch = (field, value) => setEdits((prev) => prev ? { ...prev, [field]: value } : prev);
  // Functional append used by drop-upload — avoids stale-closure issues when
  // multiple files finish out-of-order during a single drop.
  const appendImage = (url) => setEdits((prev) => prev ? { ...prev, images: [...(prev.images || []), url] } : prev);

  const handleSave = async () => {
    if (!edits) return;
    if (!edits.name?.trim()) { flashStatus('error', 'Name is required'); return; }
    setSavingRecipe(true);
    try {
      const params = {
        id: edits.id || undefined,
        slug: edits.slug || slugify(edits.name),
        name: edits.name.trim(),
        custom_title: edits.custom_title || null,
        author: edits.author || null,
        time: edits.time || null,
        introduction: edits.introduction || null,
        instructions: edits.instructions || null,
        ingredients: edits.ingredients || null,
        servings: edits.servings || null,
        images: edits.images || [],
        videos: edits.videos || [],
        skus: edits.skus || [],
        visible: !!edits.visible,
        sort_order: Number(edits.sort_order) || 0,
        published_date: edits.published_date || null,
        tag_ids: edits.tag_ids || [],
      };
      const r = await neonQuery('recipes_upsert', params);
      const saved = unwrapData(r);
      flashStatus('success', edits.id ? 'Recipe updated' : 'Recipe created');

      // Auto-publish: for VISIBLE recipes, make sure the backing BC content page
      // + atf-recipe-view template association exist on BOTH production and
      // sandbox, so saving a visible recipe makes it live at crabplace.com/{slug}/
      // (and stays on SB-2) with no manual step. Hidden/draft recipes are skipped
      // — visibility is the publish gate. This is NON-FATAL: the Neon save already
      // succeeded, so a page-ensure failure never blocks the save; we just surface
      // a small non-blocking status.
      if (saved?.visible !== false && saved?.slug && saved?.name) {
        (async () => {
          try {
            const stores = ['production', 'sandbox'];
            const outcomes = await Promise.all(stores.map(async (store) => {
              try {
                const resp = await fetch('/.netlify/functions/recipe-ensure-bc-page', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ slug: saved.slug, name: saved.name, store }),
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) return { store, ok: false, err: data.error || `HTTP ${resp.status}` };
                return { store, ok: true };
              } catch (err) {
                return { store, ok: false, err: err.message };
              }
            }));
            const failed = outcomes.filter((o) => !o.ok);
            if (failed.length) {
              flashStatus('error', `Saved, but publishing to ${failed.map((f) => f.store).join(' + ')} failed (page not live yet)`, 6000);
            }
          } catch (_) { /* never block the save */ }
        })();
      }

      // Refresh the published page body. The storefront shows what was written
      // into the BigCommerce page, not a live render, so without this an edit
      // would not reach the page until the nightly reconcile. mappedOnly limits it
      // to stores this recipe is already published to - it never creates a page
      // and never touches a store nobody published to. Non-fatal like the ensure
      // above: the Neon save has already succeeded.
      if (saved?.id) {
        fetch('/.netlify/functions/recipe-publish-page', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: saved.id, mappedOnly: true }),
        })
          .then(async (resp) => {
            if (resp.ok) return;
            const data = await resp.json().catch(() => ({}));
            const why = data.error || (data.results || []).filter((x) => !x.ok).map((x) => x.store + ': ' + x.error).join('; ') || `HTTP ${resp.status}`;
            flashStatus('error', `Saved, but refreshing the published recipe page failed (${why})`, 8000);
          })
          .catch(() => { /* never block the save */ });
      }

      loadPromotion();
      await loadRecipes();
      // Re-open with fresh data so future edits compare against the new baseline
      setSelectedId(saved.id);
      setEdits({
        ...saved,
        custom_title: saved.custom_title || '',
        author: saved.author || '',
        time: saved.time || '',
        introduction: saved.introduction || '',
        instructions: saved.instructions || '',
        ingredients: saved.ingredients || '',
        servings: saved.servings || '',
        images: saved.images || [],
        videos: saved.videos || [],
        skus: saved.skus || [],
        visible: saved.visible !== false,
        sort_order: saved.sort_order || 0,
        published_date: saved.published_date || '',
        tag_ids: derivetagIdsFromGrouped(saved.tags, tagsTree),
      });
    } catch (e) { flashStatus('error', `Save failed: ${e.message}`, 6000); }
    finally { setSavingRecipe(false); }
  };

  const handleDelete = async () => {
    if (!edits?.id) return;
    if (!window.confirm(`Delete "${edits.name}"? This can't be undone.`)) return;
    setSavingRecipe(true);
    try {
      await neonQuery('recipes_delete', { id: edits.id });
      flashStatus('success', 'Recipe deleted');
      setSelectedId(null);
      setEdits(null);
      await loadRecipes();
    } catch (e) { flashStatus('error', `Delete failed: ${e.message}`); }
    finally { setSavingRecipe(false); }
  };

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filteredRecipes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recipes.filter(r => {
      if (q && !r.name.toLowerCase().includes(q) && !r.slug?.toLowerCase().includes(q)) return false;
      if (filterVisible === 'visible' && !r.visible) return false;
      if (filterVisible === 'hidden' && r.visible) return false;
      if (filterTagIds.size) {
        const recipeTagIds = derivetagIdsFromGrouped(r.tags, tagsTree);
        for (const tid of filterTagIds) if (!recipeTagIds.includes(tid)) return false;
      }
      return true;
    });
  }, [recipes, search, filterVisible, filterTagIds, tagsTree]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1600, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: 'var(--navy)', fontWeight: 800 }}>Recipes</h1>
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{recipes.length} total · {filteredRecipes.length} shown</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setTagManagerOpen(true)} style={btnSecondary}>
          <Tag size={14} /> Manage tags
        </button>
        <button onClick={() => setImportOpen(true)} style={btnSecondary}>
          <Upload size={14} /> Import from a document
        </button>
        <button onClick={newRecipe} style={btnPrimary}>
          <Plus size={14} /> New recipe
        </button>
      </div>

      {importOpen && (
        <ImportPanel
          onClose={() => setImportOpen(false)}
          onUse={(r) => { setImportOpen(false); newRecipeFromImport(r); }}
        />
      )}

      {status && (
        <div style={{
          padding: '8px 12px', marginBottom: 12, borderRadius: 4, fontSize: 13,
          background: status.type === 'error' ? '#fee' : '#efe',
          color: status.type === 'error' ? '#c00' : '#070',
          border: `1px solid ${status.type === 'error' ? '#fcc' : '#cfc'}`,
        }}>
          {status.msg}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: edits ? '320px 1fr' : '1fr', gap: 16, alignItems: 'flex-start' }}>
        {/* List pane */}
        <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or slug..."
                style={{ ...input, paddingLeft: 28, width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11 }}>
              <select value={filterVisible} onChange={(e) => setFilterVisible(e.target.value)} style={{ ...input, padding: '4px 6px', flex: 1 }}>
                <option value="">All</option>
                <option value="visible">Visible only</option>
                <option value="hidden">Hidden only</option>
              </select>
              {filterTagIds.size > 0 && (
                <button onClick={() => setFilterTagIds(new Set())} style={{ ...btnLink, fontSize: 11 }}>
                  Clear {filterTagIds.size} tag filter{filterTagIds.size !== 1 ? 's' : ''}
                </button>
              )}
            </div>
            <details style={{ fontSize: 11 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--gray-600)' }}>Filter by tag</summary>
              <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6, paddingLeft: 4 }}>
                {tagsTree.map(cat => (
                  <div key={cat._id} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{cat.custom_title}</div>
                    {(cat.tag_rows || []).map(t => (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0', cursor: 'pointer' }}>
                        <input
                          type="checkbox" checked={filterTagIds.has(t.id)}
                          onChange={(e) => {
                            const next = new Set(filterTagIds);
                            if (e.target.checked) next.add(t.id); else next.delete(t.id);
                            setFilterTagIds(next);
                          }}
                        />
                        {t.name}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
            {filteredRecipes.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
                {search || filterVisible || filterTagIds.size ? 'No recipes match the filters.' : 'No recipes yet.'}
              </div>
            ) : (
              filteredRecipes.map(r => (
                <button
                  key={r.id} type="button"
                  onClick={() => openRecipe(r)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--gray-100)',
                    background: r.id === selectedId ? '#fff7ed' : '#fff',
                    cursor: 'pointer', fontSize: 13,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {r.images && r.images[0] ? (
                      <img src={r.images[0]} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 32, height: 32, background: 'var(--gray-100)', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageIcon size={14} style={{ color: 'var(--gray-400)' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                        /{r.slug}/ · {r.time || '—'}
                      </div>
                    </div>
                    {!r.visible && <EyeOff size={14} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Editor pane */}
        {edits && (
          <RecipeEditor
            edits={edits}
            patch={patch}
            tagsTree={tagsTree}
            bcProducts={bcProducts}
            bcLoading={bcLoading}
            saving={savingRecipe}
            isNew={!edits.id}
            onSave={handleSave}
            onDelete={handleDelete}
            promotion={edits.id ? promotion.byId[edits.id] : null}
            onPromote={(to) => promote(to, [edits.id], edits.name)}
            onClose={closeEditor}
            onOpenMediaPicker={() => setMediaPickerOpen(true)}
            onOpenVideoPicker={() => setVideoPickerOpen(true)}
            onOpenTagManager={() => setTagManagerOpen(true)}
            onAppendImage={appendImage}
            onCheckUnsaved={hasUnsavedChanges}
          />
        )}
      </div>

      {tagManagerOpen && (
        <TagManager
          tagsTree={tagsTree}
          onClose={() => setTagManagerOpen(false)}
          onChanged={() => loadTags().then(loadRecipes)}
        />
      )}

      {mediaPickerOpen && (
        <MediaPicker
          multiSelect={true}
          imagesOnly={true}
          onClose={() => setMediaPickerOpen(false)}
          onPick={(assets) => {
            const arr = Array.isArray(assets) ? assets : [assets];
            // Backstop the imagesOnly filter — never let a video URL into
            // the recipe's images array (a .mov can't render in an <img>).
            const urls = arr
              .filter(a => !/\.(mov|mp4|m4v|webm|avi|mkv)$/i.test(String(a?.r2_original_key || '')))
              .map(urlFromMediaRow)
              .filter(Boolean);
            patch('images', [...(edits?.images || []), ...urls]);
            setMediaPickerOpen(false);
          }}
        />
      )}

      {videoPickerOpen && (
        <MediaPicker
          multiSelect={true}
          videosOnly={true}
          onClose={() => setVideoPickerOpen(false)}
          onPick={(assets) => {
            const arr = Array.isArray(assets) ? assets : [assets];
            const urls = arr.map(urlFromMediaRow).filter(Boolean);
            patch('videos', [...(edits?.videos || []), ...urls]);
            setVideoPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// Resolve grouped { Category: [tagName] } → flat array of tag IDs.
function derivetagIdsFromGrouped(grouped, tagsTree) {
  if (!grouped || !tagsTree?.length) return [];
  const ids = [];
  for (const cat of tagsTree) {
    const names = grouped[cat.custom_title] || [];
    for (const n of names) {
      const t = (cat.tag_rows || []).find(x => x.name === n);
      if (t) ids.push(t.id);
    }
  }
  return ids;
}

// ── RecipeEditor ─────────────────────────────────────────────────────────────
function RecipeEditor({ edits, patch, tagsTree, bcProducts, bcLoading, saving, isNew, onSave, onDelete, onClose, onOpenMediaPicker, onOpenVideoPicker, onOpenTagManager, onAppendImage, onCheckUnsaved, promotion, onPromote }) {
  const tagIdSet = useMemo(() => new Set(edits.tag_ids || []), [edits.tag_ids]);
  const toggleTag = (tagId) => {
    const next = new Set(tagIdSet);
    if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
    patch('tag_ids', Array.from(next));
  };

  const removeImage = (idx) => patch('images', (edits.images || []).filter((_, i) => i !== idx));
  const moveImage = (idx, dir) => {
    const arr = [...(edits.images || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    patch('images', arr);
  };

  const [copiedImgIdx, setCopiedImgIdx] = useState(null);
  const copyImageUrl = useCallback(async (idx, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedImgIdx(idx);
      setTimeout(() => setCopiedImgIdx((c) => (c === idx ? null : c)), 1500);
    } catch {
      window.prompt('Copy URL:', url);
    }
  }, []);

  const previewSlug = edits.slug || slugify(edits.name);
  const [previewing, setPreviewing] = useState(false);

  // ── Upload state for drag-drop / click-to-pick ────────────────────────────
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const uploadsPending = uploads.some((u) => u.status === 'queued' || u.status === 'resizing' || u.status === 'uploading');

  const runUploads = useCallback(async (filesLike) => {
    const files = Array.from(filesLike || []);
    if (!files.length) return;
    const items = files.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name, file: f, status: 'queued', message: '',
    }));
    setUploads((prev) => [...items, ...prev]);
    const setItem = (id, p) => setUploads((prev) => prev.map(x => x.id === id ? { ...x, ...p } : x));
    for (const item of items) {
      if (/\.(heic|heif)$/i.test(item.name)) {
        setItem(item.id, { status: 'error', message: 'HEIC not supported — export as JPEG' });
        continue;
      }
      if (!/^image\//.test(item.file.type) && !/\.(jpe?g|png|webp|gif)$/i.test(item.name)) {
        setItem(item.id, { status: 'error', message: `Unsupported type: ${item.file.type || 'unknown'}` });
        continue;
      }
      try {
        setItem(item.id, { status: 'resizing' });
        const [orig, thumb] = await Promise.all([
          fileToResizedBase64(item.file, UPLOAD_ORIGINAL_MAX_EDGE, UPLOAD_ORIGINAL_QUALITY),
          fileToResizedBase64(item.file, UPLOAD_THUMB_MAX_EDGE, UPLOAD_THUMB_QUALITY),
        ]);
        setItem(item.id, { status: 'uploading' });
        const res = await fetch('/.netlify/functions/media-library-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: item.name,
            contentType: 'image/jpeg',
            // brand is required by media-library-upload's validator. Recipes
            // belong to crabplace.com so 'crab-place' is the right default.
            brand: 'crab-place',
            admin_id: getCurrentUser()?.id,
            originalBase64: orig.base64,
            thumbBase64: thumb.base64,
            width: orig.naturalWidth,
            height: orig.naturalHeight,
            extraTags: ['recipe-image'],
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        const url = urlFromMediaRow(data.row);
        if (!url) throw new Error('Upload succeeded but no R2 key in response');
        onAppendImage(url);
        setItem(item.id, {
          status: 'done',
          message: data.duplicate ? 'already in library — added' : 'uploaded',
        });
      } catch (e) {
        setItem(item.id, { status: 'error', message: (e.message || 'Upload failed').slice(0, 160) });
      }
    }
  }, [onAppendImage]);

  const onPickFiles = (e) => {
    runUploads(e.target.files);
    e.target.value = '';
  };
  const clearDoneUploads = () => setUploads((prev) => prev.filter(x => x.status !== 'done'));

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
      {/* Save bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{isNew ? 'New recipe' : `Editing #${edits.id}`}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{edits.name || '(untitled)'}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            <a href={`https://the-crab-place-sandbox-2.mybigcommerce.com/${previewSlug}/`} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)' }}>
              <Link2 size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> /{previewSlug}/
            </a>
          </div>
        </div>
        <button onClick={() => patch('visible', !edits.visible)} style={{ ...btnSecondary, color: edits.visible ? 'var(--navy)' : 'var(--gray-500)' }} title={edits.visible ? 'Visible — click to hide' : 'Hidden — click to show'}>
          {edits.visible ? <Eye size={14} /> : <EyeOff size={14} />} {edits.visible ? 'Visible' : 'Hidden'}
        </button>
        {!isNew && (
          <button onClick={onDelete} disabled={saving} style={btnDanger}>
            <Trash2 size={14} /> Delete
          </button>
        )}
        <button
          onClick={async () => {
            // Pre-save preview only reflects what's already in Neon.
            if (typeof onCheckUnsaved === 'function' && onCheckUnsaved()) {
              const ok = window.confirm("You have unsaved changes — they won't appear in the preview yet. Open the saved version anyway?");
              if (!ok) return;
            }
            setPreviewing(true);
            try {
              // Ensure the BC content page + atf-recipe-view template association
              // exist; without this the storefront URL 404s for recipes that
              // never had a backing BC page (orphans from migration or
              // admin-created recipes — auto-create wasn't wired previously).
              const r = await fetch('/.netlify/functions/recipe-ensure-bc-page', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slug: previewSlug, name: edits.name, store: 'sandbox' }),
              });
              const data = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
              const url = data.previewUrl || `https://the-crab-place-sandbox-2.mybigcommerce.com/${previewSlug}/`;
              // BC content pages take a few seconds to surface on the storefront
              // after creation. If we just created one, give it a brief moment
              // before opening so the first click doesn't 404.
              if (data.created) await new Promise((res) => setTimeout(res, 1500));
              window.open(url, '_blank', 'noopener');
            } catch (e) {
              window.alert(`Preview failed: ${e.message}`);
            } finally {
              setPreviewing(false);
            }
          }}
          disabled={!previewSlug || isNew || previewing}
          title={isNew ? 'Save the recipe first to preview' : `Open ${previewSlug} in a new tab`}
          style={btnSecondary}
        >
          <Eye size={14} /> {previewing ? 'Preparing…' : 'Preview'}
        </button>
        {!isNew && <PromoteControls promotion={promotion} saving={saving} onPromote={onPromote} />}
        <button onClick={onClose} style={btnSecondary}><X size={14} /> Close</button>
        <button
          onClick={onSave}
          disabled={saving || uploadsPending}
          title={uploadsPending ? 'Wait for uploads to finish before saving' : ''}
          style={btnPrimary}
        >
          <Save size={14} /> {saving ? 'Saving…' : uploadsPending ? 'Uploading…' : 'Save'}
        </button>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Top-row metadata */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name *">
            <input style={input} value={edits.name} onChange={(e) => patch('name', e.target.value)} placeholder="e.g. Maryland Crab Cakes" />
          </Field>
          <Field label="Slug" hint="Auto-derived from name; override if needed.">
            <input style={input} value={edits.slug || ''} onChange={(e) => patch('slug', e.target.value)} placeholder={slugify(edits.name) || 'auto-from-name'} />
          </Field>
          <Field label="Author">
            <input style={input} value={edits.author || ''} onChange={(e) => patch('author', e.target.value)} placeholder="e.g. Rebecca Bent" />
          </Field>
          <Field
            label="Prep time"
            hint={(edits.time || '').trim()
              ? 'Free-form text shown on the listing card and PDP slider.'
              : '⚠ Currently blank — listings will show "Preparation time not specified" until you fill this in and save.'
            }
          >
            <input
              style={{
                ...input,
                borderColor: (edits.time || '').trim() ? input.border : '#c63',
                background: (edits.time || '').trim() ? '#fff' : '#fff8f0',
              }}
              value={edits.time || ''}
              onChange={(e) => patch('time', e.target.value)}
              placeholder='Type here, e.g. "10 minute prep"'
              autoComplete="off"
              name="recipePrepTime"
            />
          </Field>
          <Field label="Published date">
            <input type="date" style={input} value={edits.published_date || ''} onChange={(e) => patch('published_date', e.target.value)} />
          </Field>
          <Field label="Sort order" hint="Lower number = appears first when sorting by sort_order.">
            <input type="number" style={input} value={edits.sort_order || 0} onChange={(e) => patch('sort_order', e.target.value)} />
          </Field>
        </div>

        {/* Images */}
        <Section title="Images" subtitle="First image is the card thumbnail. Drop files anywhere in the box to upload, click 'Upload file' to browse, or 'Add from library' to pick existing.">
          <div
            onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => {
              // Only clear when leaving the dropzone itself, not when dragging over a child.
              if (e.currentTarget.contains(e.relatedTarget)) return;
              setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const files = e.dataTransfer?.files;
              if (files && files.length) runUploads(files);
            }}
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 8,
              padding: 8, borderRadius: 6,
              border: dragOver ? '2px dashed var(--orange)' : '2px dashed transparent',
              background: dragOver ? 'rgba(220,88,42,0.06)' : 'transparent',
              transition: 'background 100ms, border-color 100ms',
            }}
          >
            {(edits.images || []).map((url, i) => (
              <div key={i} style={{ position: 'relative', width: 100, height: 100, border: '1px solid var(--gray-200)', borderRadius: 4, overflow: 'hidden', background: '#fafafa' }}>
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 2 }}>
                  <button type="button" onClick={() => moveImage(i, -1)} title="Move left" style={{ ...iconBtn, opacity: i === 0 ? 0.4 : 1 }} disabled={i === 0}>‹</button>
                  <button type="button" onClick={() => moveImage(i, 1)} title="Move right" style={{ ...iconBtn, opacity: i === (edits.images.length - 1) ? 0.4 : 1 }} disabled={i === (edits.images.length - 1)}>›</button>
                  <button type="button" onClick={() => copyImageUrl(i, url)} title={copiedImgIdx === i ? 'Copied!' : url} style={{ ...iconBtn, color: copiedImgIdx === i ? '#080' : 'var(--gray-700)' }}>
                    {copiedImgIdx === i ? <Check size={11} /> : <Link2 size={11} />}
                  </button>
                  <button type="button" onClick={() => removeImage(i)} title="Remove" style={{ ...iconBtn, color: '#c00' }}>×</button>
                </div>
                {i === 0 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: 9, padding: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', textAlign: 'center', textTransform: 'uppercase' }}>Hero</div>}
              </div>
            ))}
            <button type="button" onClick={() => fileInputRef.current?.click()} style={{ width: 100, height: 100, border: '2px dashed var(--gray-300)', borderRadius: 4, background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--gray-600)', fontSize: 11 }}>
              <Upload size={20} /> Upload file
            </button>
            <button type="button" onClick={onOpenMediaPicker} style={{ width: 100, height: 100, border: '2px dashed var(--gray-300)', borderRadius: 4, background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--gray-500)', fontSize: 11 }}>
              <Plus size={20} /> Add from library
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onPickFiles} />
          </div>
          {uploads.length > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#fafafa', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Uploads</div>
                <div style={{ flex: 1 }} />
                {uploads.some(u => u.status === 'done') && (
                  <button type="button" onClick={clearDoneUploads} style={{ ...btnSecondary, padding: '2px 8px', fontSize: 11 }}>Clear done</button>
                )}
              </div>
              {uploads.map(u => (
                <div key={u.id} style={{ fontSize: 11, display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
                  <span style={{ flex: '0 1 240px', fontWeight: 600, color: 'var(--gray-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                  <span style={{
                    color: u.status === 'error' ? '#c00' : u.status === 'done' ? '#080' : 'var(--gray-500)',
                  }}>{u.message || u.status}</span>
                </div>
              ))}
            </div>
          )}
          {(edits.images || []).length > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#fafafa', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Image URLs</div>
              {(edits.images || []).map((url, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
                  <span style={{ flex: '0 0 28px', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)' }}>{i === 0 ? 'Hero' : `#${i + 1}`}</span>
                  <input
                    type="text"
                    readOnly
                    value={url}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: 1, fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', padding: '3px 6px', border: '1px solid var(--gray-200)', borderRadius: 3, background: '#fff', color: 'var(--gray-700)' }}
                  />
                  <button
                    type="button"
                    onClick={() => copyImageUrl(i, url)}
                    title={copiedImgIdx === i ? 'Copied!' : 'Copy URL'}
                    style={{ ...btnSecondary, padding: '3px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, color: copiedImgIdx === i ? '#080' : undefined }}
                  >
                    {copiedImgIdx === i ? <Check size={12} /> : <Link2 size={12} />}
                    {copiedImgIdx === i ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Three rich-text editors */}
        <Section title="Introduction" subtitle="Lead paragraph above the recipe.">
          <ReactQuill theme="snow" modules={QUILL_MODULES} formats={QUILL_FORMATS} value={edits.introduction || ''} onChange={(v) => patch('introduction', v)} />
        </Section>
        <Section title="Serving Size" subtitle="Free-form text shown between the introduction and ingredients (e.g. 'Serves 4' or 'Makes 4 to 6 servings').">
          <input
            style={{ ...input, width: '100%', maxWidth: 360 }}
            value={edits.servings || ''}
            onChange={(e) => patch('servings', e.target.value)}
            placeholder="e.g. Makes 4 to 6 servings"
          />
        </Section>
        <Section title="Ingredients" subtitle='Use the bulleted list button. Each line will render as a separate ingredient.'>
          <ReactQuill theme="snow" modules={QUILL_MODULES} formats={QUILL_FORMATS} value={edits.ingredients || ''} onChange={(v) => patch('ingredients', v)} />
        </Section>
        <Section title="Instructions" subtitle="Numbered list recommended (toolbar 1. button).">
          <ReactQuill theme="snow" modules={QUILL_MODULES} formats={QUILL_FORMATS} value={edits.instructions || ''} onChange={(v) => patch('instructions', v)} />
        </Section>

        {/* Tags */}
        <Section
          title="Tags"
          subtitle="These drive the sidebar facets on /recipes/. Click chips to assign/unassign for this recipe; click 'Manage tags' to add, rename, reorder, or delete categories and tags."
          action={
            <button type="button" onClick={onOpenTagManager} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
              <Tag size={12} /> Manage tags
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tagsTree.map(cat => (
              <div key={cat._id}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{cat.custom_title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(cat.tag_rows || []).map(t => {
                    const on = tagIdSet.has(t.id);
                    return (
                      <button key={t.id} type="button" onClick={() => toggleTag(t.id)} style={{
                        padding: '4px 10px', borderRadius: 12, fontSize: 12, cursor: 'pointer',
                        border: on ? '1px solid var(--orange)' : '1px solid var(--gray-300)',
                        background: on ? 'var(--orange)' : '#fff',
                        color: on ? '#fff' : 'var(--gray-700)',
                      }}>
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* SKU typeahead */}
        <Section title="Linked products (SKUs)" subtitle="Recipes appear on the PDP of each linked product. Type to search the BC catalog.">
          <SkuPicker
            value={edits.skus || []}
            onChange={(next) => patch('skus', next)}
            bcProducts={bcProducts}
            bcLoading={bcLoading}
          />
        </Section>

        {/* Video. Rebecca, 2026-09-09: "we don't have a place for the video
            of the recipe." There WAS a box, but it only took a pasted URL and
            only YouTube actually played, so a clip she had shot herself had
            nowhere to go. */}
        <Section title="Video" subtitle="Pick a clip from the Media Library, or paste a YouTube link. The first one plays on the recipe page.">
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={onOpenVideoPicker} style={btnSecondary}>
              <Plus size={14} /> Add from library
            </button>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              Upload the clip in Marketing &gt; Media Library first, then pick it here.
            </span>
          </div>
          {(edits.videos || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
              {(edits.videos || []).map((v, i) => (
                <div key={v + i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, background: 'var(--gray-100, #f0f0f3)', padding: '4px 8px', borderRadius: 4 }}>
                  <span style={{ flex: 1, wordBreak: 'break-all' }}>
                    {isYouTubeUrl(v) ? 'YouTube: ' : 'Clip: '}{v.length > 90 ? v.slice(0, 90) + '\u2026' : v}
                  </span>
                  <button type="button" title="Remove" onClick={() => patch('videos', (edits.videos || []).filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', display: 'inline-flex' }}>
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            rows={2} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }}
            value={(edits.videos || []).join('\n')}
            onChange={(e) => patch('videos', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </Section>
      </div>
    </div>
  );
}

// ── SkuPicker (typeahead) ────────────────────────────────────────────────────
// Promote one recipe along SB-2 -> SB-1 -> PROD, with where it stands now.
// "Promote to PROD" stays disabled until the recipe is on SB-1, because
// production only ever receives what SB-1 already has.
function PromoteControls({ promotion, saving, onPromote }) {
  const [busy, setBusy] = useState(false);
  const p = promotion || {};
  const sb1 = !p.sb1_promoted_at ? { text: 'SB-1: not promoted', color: '#9a6700' }
    : p.sb1_stale ? { text: 'SB-1: behind', color: '#9a6700' } : { text: 'SB-1: up to date', color: '#1a7f37' };
  const prod = !p.prod_promoted_at ? { text: 'PROD: not promoted', color: '#9a6700' }
    : p.prod_stale ? { text: 'PROD: behind SB-1', color: '#9a6700' } : { text: 'PROD: up to date', color: '#1a7f37' };
  const when = (at, by) => (at ? 'Last promoted ' + new Date(at).toLocaleString() + (by ? ' by ' + by : '') : 'Never promoted');
  const run = async (to) => { setBusy(true); try { await onPromote(to); } finally { setBusy(false); } };
  const tag = { fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
  const btn = { fontSize: 12, padding: '6px 10px', border: '1px solid #c9d1d9', borderRadius: 6, background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ ...tag, color: sb1.color }} title={when(p.sb1_promoted_at, p.sb1_promoted_by)}>{sb1.text}</span>
      <button type="button" style={btn} disabled={saving || busy} onClick={() => run('sb1')} title="Copy the saved SB-2 version to SB-1">
        {busy ? 'Working...' : 'Promote to SB-1'}
      </button>
      <span style={{ ...tag, color: prod.color }} title={when(p.prod_promoted_at, p.prod_promoted_by)}>{prod.text}</span>
      <button type="button" style={btn} disabled={saving || busy || !p.sb1_promoted_at} onClick={() => run('prod')}
        title={p.sb1_promoted_at ? 'Copy the SB-1 version to production' : 'Promote to SB-1 first'}>
        Promote to PROD
      </button>
    </div>
  );
}

function SkuPicker({ value, onChange, bcProducts, bcLoading }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const linked = new Set((value || []).map(v => String(v.sku)));
    return bcProducts
      .filter(p => !linked.has(String(p.sku)))
      .filter(p => p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, bcProducts, value]);

  const add = (p) => {
    // url: stored alongside sku/name so the storefront recipe page can link
    // straight to the PDP. /search.php?search_query= only auto-redirects on a
    // unique match — partial-match SKUs (LOB, LVPMHC...) land on a PCP.
    onChange([...(value || []), { sku: p.sku, name: p.name, url: p.url || '' }]);
    setQuery(''); setOpen(false);
    inputRef.current?.focus();
  };
  const remove = (sku) => onChange((value || []).filter(v => v.sku !== sku));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {(value || []).map(s => (
          <span key={s.sku} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px 3px 10px', background: 'var(--navy)', color: '#fff', borderRadius: 12, fontSize: 12 }}>
            <strong style={{ fontFamily: 'monospace' }}>{s.sku}</strong>
            <span style={{ opacity: 0.85 }}>· {s.name}</span>
            <button type="button" onClick={() => remove(s.sku)} style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 14 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text" value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} // delay so click on option fires
          placeholder={bcLoading ? 'Loading catalog...' : `Search ${bcProducts.length} products by name or SKU...`}
          disabled={bcLoading}
          style={{ ...input, width: '100%' }}
        />
        {open && matches.length > 0 && (
          <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 4, marginTop: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxHeight: 280, overflowY: 'auto' }}>
            {matches.map(p => (
              <button
                key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(p)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: '#fff', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}
              >
                <span style={{ fontFamily: 'monospace', color: 'var(--orange)', fontSize: 11, marginRight: 6 }}>{p.sku || '(no sku)'}</span>
                {p.name}
              </button>
            ))}
          </div>
        )}
        {open && query && matches.length === 0 && (
          <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 4, marginTop: 2, padding: '8px 12px', fontSize: 12, color: 'var(--gray-500)' }}>
            No matches.
          </div>
        )}
      </div>
    </div>
  );
}

// ── TagManager modal ─────────────────────────────────────────────────────────
function TagManager({ tagsTree, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const upsertCategory = async (id, custom_title, category_name, sort_order) => {
    setBusy(true); setErr(null);
    try {
      await neonQuery('recipe_tags_admin', { op: 'category_upsert', id, custom_title, category_name, sort_order });
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category "${name}" and all its tags?`)) return;
    setBusy(true); setErr(null);
    try {
      await neonQuery('recipe_tags_admin', { op: 'category_delete', id });
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const upsertTag = async (id, category_id, name) => {
    setBusy(true); setErr(null);
    try {
      await neonQuery('recipe_tags_admin', { op: 'tag_upsert', id, category_id, name });
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const deleteTag = async (id, name) => {
    if (!window.confirm(`Delete tag "${name}"?`)) return;
    setBusy(true); setErr(null);
    try {
      await neonQuery('recipe_tags_admin', { op: 'tag_delete', id });
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--navy)' }}>Manage tags</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnSecondary}><X size={14} /> Done</button>
        </div>
        {err && <div style={{ margin: 14, padding: 10, background: '#fee', color: '#c00', borderRadius: 4, fontSize: 13 }}>{err}</div>}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {tagsTree.map(cat => (
            <CategoryEditor
              key={cat._id} category={cat} busy={busy}
              onUpdateCategory={(updates) => upsertCategory(cat.id || Number(cat._id), updates.custom_title, updates.category_name, updates.sort_order)}
              onDeleteCategory={() => deleteCategory(cat.id || Number(cat._id), cat.custom_title)}
              onAddTag={(name) => upsertTag(null, cat.id || Number(cat._id), name)}
              onRenameTag={(tagId, name) => upsertTag(tagId, cat.id || Number(cat._id), name)}
              onDeleteTag={(tagId, name) => deleteTag(tagId, name)}
            />
          ))}
          <NewCategoryForm onCreate={(custom_title, category_name) => upsertCategory(null, custom_title, category_name, tagsTree.length)} busy={busy} />
        </div>
      </div>
    </div>
  );
}

function CategoryEditor({ category, busy, onUpdateCategory, onDeleteCategory, onAddTag, onRenameTag, onDeleteTag }) {
  const [titleEdit, setTitleEdit] = useState(category.custom_title);
  const [newTag, setNewTag] = useState('');
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6 }}>
      <div style={{ padding: '8px 12px', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <input
          style={{ ...input, flex: 1 }} value={titleEdit}
          onChange={(e) => setTitleEdit(e.target.value)}
          onBlur={() => { if (titleEdit !== category.custom_title) onUpdateCategory({ custom_title: titleEdit, category_name: category.category_name, sort_order: category.sort_order }); }}
        />
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{(category.tag_rows || []).length} tags</span>
        <button onClick={onDeleteCategory} disabled={busy} style={{ ...iconBtn, color: '#c00' }} title="Delete category"><Trash2 size={12} /></button>
      </div>
      {expanded && (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(category.tag_rows || []).map(t => (
            <TagRow key={t.id} tag={t} busy={busy} onRename={(name) => onRenameTag(t.id, name)} onDelete={() => onDeleteTag(t.id, t.name)} />
          ))}
          <div style={{ display: 'flex', gap: 6, paddingTop: 4 }}>
            <input
              style={{ ...input, flex: 1 }} value={newTag} onChange={(e) => setNewTag(e.target.value)}
              placeholder="New tag name..." onKeyDown={(e) => { if (e.key === 'Enter' && newTag.trim()) { onAddTag(newTag.trim()); setNewTag(''); } }}
            />
            <button type="button" onClick={() => { if (newTag.trim()) { onAddTag(newTag.trim()); setNewTag(''); } }} disabled={busy || !newTag.trim()} style={btnPrimary}><Plus size={12} /> Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TagRow({ tag, busy, onRename, onDelete }) {
  const [name, setName] = useState(tag.name);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        style={{ ...input, flex: 1 }} value={name} onChange={(e) => setName(e.target.value)}
        onBlur={() => { if (name !== tag.name && name.trim()) onRename(name.trim()); }}
      />
      <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--gray-500)' }}>{tag.slug}</span>
      <button onClick={onDelete} disabled={busy} style={{ ...iconBtn, color: '#c00' }} title="Delete tag"><Trash2 size={12} /></button>
    </div>
  );
}

function NewCategoryForm({ onCreate, busy }) {
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const submit = () => {
    if (!title.trim() || !name.trim()) return;
    onCreate(title.trim(), name.trim());
    setTitle(''); setName('');
  };
  return (
    <div style={{ border: '1px dashed var(--gray-300)', borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Add new category</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
        <input style={input} placeholder="Display title (e.g. Cuisine)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input style={input} placeholder="Internal key (e.g. cuisine)" value={name} onChange={(e) => setName(e.target.value)} />
        <button onClick={submit} disabled={busy || !title.trim() || !name.trim()} style={btnPrimary}><Plus size={12} /> Add category</button>
      </div>
    </div>
  );
}

// ── Layout helpers ───────────────────────────────────────────────────────────
function Section({ title, subtitle, action, children }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: subtitle ? 2 : 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{title}</div>
        <div style={{ flex: 1 }} />
        {action}
      </div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>{subtitle}</div>}
      {children}
    </div>
  );
}
function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// ── Style tokens (match CPx app conventions) ─────────────────────────────────
const input = {
  padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 4,
  fontSize: 13, background: '#fff',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', background: 'var(--orange)', color: '#fff',
  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', background: '#fff', color: 'var(--navy)',
  border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
const btnDanger = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', background: '#fff', color: '#c00',
  border: '1px solid #fcc', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};
const btnLink = {
  background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0, textDecoration: 'underline',
};
const iconBtn = {
  width: 18, height: 18, padding: 0, border: 'none', background: 'rgba(255,255,255,0.85)',
  borderRadius: 3, cursor: 'pointer', fontSize: 12, lineHeight: 1, display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
};


// ── ImportPanel ──────────────────────────────────────────────────────────────
//
// Rebecca, 2026-09-09: "it takes a lot of time to do it the way it is currently
// formatted. If I could upload and the formatting happens automatically and I
// can edit it from there, we are saving a ton of time."
//
// ⚠️ THIS PANEL NEVER SAVES. It parses and hands the result to the editor as an
// unsaved draft. The whole value of the feature is that she reads it back and
// fixes it, so a one-click "import and publish" would remove the only step that
// catches a bad parse.
//
// ⚠️ WHAT IT COULD NOT FIND IS SHOWN, NOT HIDDEN. recipe-parse returns empty
// strings rather than invented values, and `missing` names them. A blank
// cooking time is obviously unfinished; a confidently wrong one is not.
function ImportPanel({ onClose, onUse }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const readAsBase64 = (f) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).slice(String(fr.result).indexOf(',') + 1));
    fr.onerror = () => reject(new Error('Could not read that file'));
    fr.readAsDataURL(f);
  });

  const run = async () => {
    setErr(null); setResult(null); setBusy(true);
    try {
      const body = file
        ? { filename: file.name, base64: await readAsBase64(file) }
        : { text };
      const res = await fetch('/.netlify/functions/recipe-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getAccessToken() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || ('HTTP ' + res.status));
      setResult(data);
    } catch (e) {
      setErr(e.message || 'Could not read that');
    } finally {
      setBusy(false);
    }
  };

  const canRun = !busy && (file || text.trim().length > 20);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 60,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div style={{ background: '#fff', borderRadius: 8, width: '100%', maxWidth: 720, padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--navy)', fontWeight: 800 }}>Import from a document</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', display: 'inline-flex' }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--gray-500)' }}>
          Drop a Word document, a PDF, or a photo of a page, or paste the text. It fills the recipe form in for you.
          Nothing is saved until you have read it over and pressed Save.
        </p>

        <div
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files && e.dataTransfer.files[0];
            if (f) { setFile(f); setText(''); setResult(null); setErr(null); }
          }}
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: '2px dashed ' + (dragOver ? 'var(--orange)' : 'var(--gray-300)'),
            background: dragOver ? '#fff7f3' : '#fafafa',
            borderRadius: 6, padding: '20px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 10,
          }}
        >
          <Upload size={20} style={{ color: 'var(--gray-500)' }} />
          <div style={{ fontSize: 13, marginTop: 6, color: 'var(--navy)', fontWeight: 600 }}>
            {file ? file.name : 'Drop the file here, or click to choose one'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
            Word (.docx), PDF, or a photo of the page
          </div>
          <input
            ref={fileRef} type="file" style={{ display: 'none' }}
            accept=".docx,.pdf,.txt,.md,.csv,image/*"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) { setFile(f); setText(''); setResult(null); setErr(null); }
            }}
          />
        </div>

        <div style={{ fontSize: 11, color: 'var(--gray-500)', textAlign: 'center', margin: '8px 0' }}>or paste the recipe</div>
        <textarea
          rows={5} value={text}
          onChange={(e) => { setText(e.target.value); if (e.target.value) setFile(null); setResult(null); }}
          placeholder="Paste the whole recipe here, however it is written."
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
        />

        {err && (
          <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 4, background: '#fee', color: '#c00', fontSize: 13 }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button onClick={run} disabled={!canRun} style={{ ...btnPrimary, opacity: canRun ? 1 : 0.5, cursor: canRun ? 'pointer' : 'default' }}>
            {busy ? 'Reading it...' : 'Read it'}
          </button>
          {file && <button onClick={() => setFile(null)} style={btnSecondary}>Clear file</button>}
          {busy && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>This takes a few seconds.</span>}
        </div>

        {result && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
              Read as {result.how}. Found {result.recipes.length} recipe{result.recipes.length === 1 ? '' : 's'}.
            </div>
            {result.warning && (
              <div style={{ padding: '8px 12px', borderRadius: 4, background: '#fffbe6', color: '#6b5800', fontSize: 13, marginBottom: 8 }}>{result.warning}</div>
            )}
            {result.recipes.map((r, i) => (
              <div key={i} style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>{r.name || '(no name found)'}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 3 }}>
                  {r.counts.ingredients} ingredient{r.counts.ingredients === 1 ? '' : 's'} ·{' '}
                  {r.counts.instructions} step{r.counts.instructions === 1 ? '' : 's'}
                  {r.counts.notes ? ' · ' + r.counts.notes + ' note' + (r.counts.notes === 1 ? '' : 's') : ''}
                  {r.time ? ' · ' + r.time : ''}
                  {r.servings ? ' · ' + r.servings : ''}
                </div>
                {r.missing.length > 0 && (
                  <div style={{ fontSize: 12, color: '#8a6d00', background: '#fffbe6', padding: '5px 8px', borderRadius: 3, marginTop: 6 }}>
                    Not in the document: {r.missing.join(', ')}. You will need to fill those in.
                  </div>
                )}
                {r.suggestedTags.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                    Suggested tags: {r.suggestedTags.join(', ')} (pick the real ones in the editor)
                  </div>
                )}
                <button onClick={() => onUse(r)} style={{ ...btnPrimary, marginTop: 10 }}>
                  <Check size={14} /> Open in the editor
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
